import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * @component LoadingSpinner
 * @description Componente de spinner de carga reutilizable
 */

interface LoadingSpinnerProps {
  /** Tamaño del spinner */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Mensaje a mostrar debajo del spinner */
  message?: string;
  /** Color del spinner */
  color?: 'primary' | 'white' | 'gray';
  /** Clases CSS adicionales */
  className?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  message,
  color = 'primary',
  className = '',
}) => {
  // Clases de tamaño
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'h-4 w-4';
      case 'lg':
        return 'h-8 w-8';
      case 'xl':
        return 'h-12 w-12';
      default:
        return 'h-6 w-6';
    }
  };

  // Clases de color
  const getColorClasses = () => {
    switch (color) {
      case 'white':
        return 'text-white';
      case 'gray':
        return 'text-gray-600';
      default:
        return 'text-primary-600';
    }
  };

  // Tamaño del texto del mensaje
  const getMessageSize = () => {
    switch (size) {
      case 'sm':
        return 'text-xs';
      case 'lg':
        return 'text-base';
      case 'xl':
        return 'text-lg';
      default:
        return 'text-sm';
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <Loader2 
        className={`animate-spin ${getSizeClasses()} ${getColorClasses()}`}
      />
      {message && (
        <p className={`mt-3 text-gray-600 text-center ${getMessageSize()}`}>
          {message}
        </p>
      )}
    </div>
  );
};

export default LoadingSpinner;

/**
 * @component FullPageLoader
 * @description Loader que ocupa toda la pantalla
 */

interface FullPageLoaderProps {
  message?: string;
  size?: LoadingSpinnerProps['size'];
}

export const FullPageLoader: React.FC<FullPageLoaderProps> = ({
  message = 'Cargando...',
  size = 'lg',
}) => {
  return (
    <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
      <LoadingSpinner size={size} message={message} />
    </div>
  );
};

/**
 * @component InlineLoader
 * @description Loader pequeño para usar inline
 */

interface InlineLoaderProps {
  message?: string;
  className?: string;
}

export const InlineLoader: React.FC<InlineLoaderProps> = ({
  message,
  className = '',
}) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
      {message && (
        <span className="text-sm text-gray-600">{message}</span>
      )}
    </div>
  );
};

/**
 * @component ButtonLoader
 * @description Loader para usar dentro de botones
 */

interface ButtonLoaderProps {
  message?: string;
  color?: 'white' | 'primary';
}

export const ButtonLoader: React.FC<ButtonLoaderProps> = ({
  message = 'Cargando...',
  color = 'white',
}) => {
  return (
    <div className="flex items-center gap-2">
      <Loader2 className={`h-4 w-4 animate-spin ${color === 'white' ? 'text-white' : 'text-primary-600'}`} />
      <span className={color === 'white' ? 'text-white' : 'text-primary-600'}>
        {message}
      </span>
    </div>
  );
};
